import React, { useState, useRef } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import TotalModalWrapper from '../../Model/Share/Normal/TotalModalWrapper';

const PIN_LENGTH = 4;

export default function MainSecondPasswordEditModal(props) {
  const { onClickExit, onClickSave } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [focusedRow, setFocusedRow] = useState(null);

  const currentRef = useRef();
  const newRef = useRef();
  const confirmRef = useRef();

  const isNewFilled = newPin.length === PIN_LENGTH;
  const isConfirmFilled = confirmPin.length === PIN_LENGTH;
  const isMatch = isNewFilled && isConfirmFilled && newPin === confirmPin;
  const isCurrentFilled = currentPin.length === PIN_LENGTH;
  const canSave = isCurrentFilled && isNewFilled && isConfirmFilled && isMatch;

  // 숫자 입력 - 섹션당 단일 입력이라 타이핑·붙여넣기·연속 입력·백스페이스가 브라우저 기본 동작으로 자연스럽게 처리됨 //
  function handleChange(row, e) {
    const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, PIN_LENGTH);

    if (row === 'current') {
      setCurrentPin(digits);
      if (digits.length === PIN_LENGTH) newRef.current.focus();
    }
    else if (row === 'new') {
      setNewPin(digits);
      if (digits.length === PIN_LENGTH) confirmRef.current.focus();
    }
    else {
      setConfirmPin(digits);
    }
  }

  // PIN 필드 한 세트 (라벨 + 투명 입력 1개 + 표시용 4셀) - 컴포넌트가 아닌 렌더 함수라 리마운트 없음 //
  function renderPinField(row, label, value, inputRef) {
    return (
      <FieldGroup>
        <Text
          fontFamily='NotoSansKR-Medium'
          fontSize={16}
          color={theme.text}>
          {label}
        </Text>

        <PinRow onMouseDown={(e) => { e.preventDefault(); inputRef.current.focus(); }}>
          <OverlayInput
            ref={inputRef}
            type='text'
            inputMode='numeric'
            autoComplete='off'
            value={value}
            onChange={(e) => handleChange(row, e)}
            onFocus={() => setFocusedRow(row)}
            onBlur={() => setFocusedRow(null)} />
          {Array.from({ length: PIN_LENGTH }).map((_, idx) => (
            <PinCell
              key={'PinCell-' + row + '-' + idx}
              borderColor={theme.primary}
              isActive={focusedRow === row && (idx === value.length || (value.length === PIN_LENGTH && idx === PIN_LENGTH - 1))}
              activeShadow={theme.primaryAlpha20}>
              <PinChar color={theme.primary}>{value[idx] || ''}</PinChar>
            </PinCell>
          ))}
        </PinRow>
      </FieldGroup>
    );
  }

  // 부모가 API 호출 후 성공 시 모달을 닫음 (콜백 미지정 시는 즉시 닫기) //
  function onClickSaveButton() {
    if (!canSave) return;

    if (onClickSave) {
      onClickSave(currentPin, newPin);
      return;
    }
    
    onClickExit();
  }

  // 버튼 라벨 결정 //
  function decideSaveButtonLabel() {
    if (isNewFilled && isConfirmFilled && !isMatch) return t('myStoreSetting.account.secondPasswordEditModal.mismatch');

    return t('myStoreSetting.account.secondPasswordEditModal.save');
  }

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <HeaderRow>
          <HeaderColumn>
            <Text
              fontFamily='NotoSansKR-Bold'
              fontSize={16}
              color={theme.text}>
              {t('myStoreSetting.account.secondPasswordEditModal.title')}
            </Text>
            <Text
              fontFamily='NotoSansKR-Regular'
              fontSize={12}
              color={theme.subText}>
              {t('myStoreSetting.account.secondPasswordEditModal.description')}
            </Text>
          </HeaderColumn>
          <CancelChip backgroundColor={theme.buttonFill} onClick={onClickExit}>
            <Text
              fontFamily='NotoSansKR-Medium'
              fontSize={14}
              color={theme.text}>
              {t('myStoreSetting.account.secondPasswordEditModal.cancel')}
            </Text>
          </CancelChip>
        </HeaderRow>

        <PinFieldGroupWrapper>
          {renderPinField('current', t('myStoreSetting.account.secondPasswordEditModal.currentLabel'), currentPin, currentRef)}
          {renderPinField('new', t('myStoreSetting.account.secondPasswordEditModal.newLabel'), newPin, newRef)}
          {renderPinField('confirm', t('myStoreSetting.account.secondPasswordEditModal.confirmLabel'), confirmPin, confirmRef)}
        </PinFieldGroupWrapper>

        <StateButton
          gradient={theme.primaryGradient}
          onClick={onClickSaveButton}>
          <Text
            fontFamily='NotoSansKR-Bold'
            fontSize={16}
            color={theme.subBackground}>
            {decideSaveButtonLabel()}
          </Text>
        </StateButton>
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 30px;

  width: 305px;

  padding: 20px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  gap: 10px;

  width: 100%;
`;

const HeaderColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 4px;
`;

const CancelChip = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 62px;
  height: 40px;

  padding: 4px 10px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const PinFieldGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  margin-bottom: 5px;

  width: 100%;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 100%;
`;

const PinRow = styled.div`
  position: relative;

  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 8px;

  width: 100%;

  &:hover {
    cursor: text;
  }
`;

// 실제 입력을 받는 투명 오버레이 - 행 전체를 덮어 클릭/타이핑/붙여넣기를 단일 필드로 처리 //
const OverlayInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  margin: 0;
  padding: 0;
  border: 0;

  background-color: transparent;
  color: transparent;
  caret-color: transparent;

  text-align: center;

  outline: none;

  &:focus {
    outline: none;
  }
`;

const PinCell = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 0 0 60px;

  width: 60px;
  height: 60px;
  min-width: 0;
  box-sizing: border-box;

  border-radius: 10px;
  border: 2px solid ${props => props.borderColor};

  background-color: transparent;

  box-shadow: ${props => props.isActive ? `0 0 0 3px ${props.activeShadow}` : `none`};

  transition: box-shadow 0.2s ease-in-out;
`;

const PinChar = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 20px;

  margin: 0;

  color: ${props => props.color};
`;

const StateButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 40px;

  border-radius: 7px;
  background: ${props => props.gradient};

  margin-top: 8px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};
  background-color: ${props => props.backgroundColor || 'transparent'};

  margin: 0 0 0 0;
`;
